import React, { useState, useEffect } from "react";
import { useBluelagoon } from "@bluelagoon";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@shadcn-in-nx/ui/button";
import { Badge } from "@shadcn-in-nx/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@shadcn-in-nx/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@shadcn-in-nx/ui/alert";
import {
    CheckCircle,
    AlertCircle,
    Plus,
    Workflow,
    Loader2,
    X,
    LogIn,
    Star,
    Clock,
    Users,
    ZoomIn,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { NewIntegrationModal } from "../Integrations/NewIntegrationModal";
import { INTEGRATION_TYPES } from "../Integrations/Integrations";
import { useAuth } from "../context/AuthContext";
import BlueprintBadgeToggle from "./BlueprintBadgeToggle";
import BlueprintImageLightbox from "./BlueprintImageLightbox";

export function BlueprintWorkflowModal({
    isOpen,
    onClose,
    onWorkflowCreated,
    blueprint,
    onBadgeUpdated,
}) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user, setShowAuthModal } = useAuth();
    const [checkingIntegrations, setCheckingIntegrations] = useState(false);
    const [integrationStatus, setIntegrationStatus] = useState(null);
    const [showIntegrationModal, setShowIntegrationModal] = useState(false);
    const [selectedIntegrationType, setSelectedIntegrationType] =
        useState(null);
    const [missingIntegrationsToSetup, setMissingIntegrationsToSetup] =
        useState([]);
    const [currentSetupIndex, setCurrentSetupIndex] = useState(0);
    const [lightboxOpen, setLightboxOpen] = useState(false);

    // Get integrations list
    const { call: getIntegrations } = useBluelagoon({
        method: "integrationsList",
    });

    // Create workflow from blueprint
    const { call: createWorkflowFromBlueprint, isLoading: creatingWorkflow } =
        useBluelagoon({
            method: "createWorkflowFromBlueprint",
        });

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setCheckingIntegrations(false);
            setIntegrationStatus(null);
            setShowIntegrationModal(false);
            setSelectedIntegrationType(null);
            setMissingIntegrationsToSetup([]);
            setCurrentSetupIndex(0);
            setLightboxOpen(false);
        }
    }, [isOpen]);

    // Check integrations when modal opens
    useEffect(() => {
        if (isOpen && blueprint && user) {
            checkIntegrationRequirements();
        }
    }, [isOpen, blueprint, user]);

    const checkIntegrationRequirements = async () => {
        setCheckingIntegrations(true);

        try {
            // Get current integrations
            const integrationsList = await getIntegrations({
                limit: 10000,
            });

            const connectedIntegrations = integrationsList?.data || [];
            const requiredTypes = blueprint.integrationTypes || [];

            if (requiredTypes.length === 0) {
                // No integrations required
                setIntegrationStatus({
                    hasRequiredIntegrations: true,
                    missingIntegrations: [],
                    availableIntegrations: [],
                });
                setCheckingIntegrations(false);
                return;
            }

            // Check which required integrations are available
            const availableTypes = connectedIntegrations.map(
                (integration) => integration.type
            );
            const missingTypes = requiredTypes.filter(
                (type) => !availableTypes.includes(type)
            );

            setIntegrationStatus({
                hasRequiredIntegrations: missingTypes.length === 0,
                missingIntegrations: missingTypes,
                availableIntegrations: connectedIntegrations.filter(
                    (integration) => requiredTypes.includes(integration.type)
                ),
                totalRequired: requiredTypes.length,
                totalAvailable: requiredTypes.filter((type) =>
                    availableTypes.includes(type)
                ).length,
                totalMissing: missingTypes.length,
            });

            if (missingTypes.length > 0) {
                setMissingIntegrationsToSetup(missingTypes);
                setCurrentSetupIndex(0);
            }
        } catch (error) {
            console.error("Failed to check integration requirements:", error);
        }

        setCheckingIntegrations(false);
    };

    const handleCreateWorkflow = async () => {
        try {
            const result = await createWorkflowFromBlueprint({
                blueprintId: blueprint.id,
                name: `${blueprint.name} - Copy`,
            });

            if (result?.data?.workflowId) {
                onWorkflowCreated(result.data.workflowId, blueprint);
            } else if (result?.message) {
                toast.error(result.message);
            }
        } catch (error) {
            console.error("Failed to create workflow from blueprint:", error);
            toast.error(
                t(
                    "blueprints.errorCreatingWorkflow",
                    "Failed to create workflow"
                )
            );
        }
    };

    const handleSetupNextIntegration = () => {
        onClose();
        navigate("/integrate", {
            state: {
                missingIntegrations: missingIntegrationsToSetup,
                blueprintName: blueprint?.name,
            },
        });
    };

    const handleIntegrationCreated = () => {
        // Move to next missing integration or recheck status
        const nextIndex = currentSetupIndex + 1;
        setCurrentSetupIndex(nextIndex);

        if (nextIndex >= missingIntegrationsToSetup.length) {
            // All integrations set up, recheck status
            checkIntegrationRequirements();
        } else {
            // Continue with next integration
            handleSetupNextIntegration();
        }
    };

    const getIntegrationDisplayName = (integrationType) => {
        const integration = INTEGRATION_TYPES.find(
            (type) => type.id === integrationType
        );
        return integration?.name || integrationType;
    };

    const handleLoginClick = () => {
        setShowAuthModal(true);
        onClose();
    };

    // If user is not logged in, show login modal
    if (!user) {
        return (
            <>
            <BlueprintImageLightbox
                imageUrl={blueprint?.imageUrl}
                alt={`${blueprint?.name || ""} blueprint`}
                isOpen={lightboxOpen}
                onClose={() => setLightboxOpen(false)}
            />
            <Dialog open={isOpen} onOpenChange={onClose}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-xl sm:text-2xl">
                            <Workflow className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
                            <span className="break-words">
                                {blueprint?.name}
                            </span>
                        </DialogTitle>
                        <DialogDescription className="text-sm sm:text-base text-gray-600">
                            {blueprint?.description ||
                                t(
                                    "blueprints.noDescription",
                                    "No description available"
                                )}
                        </DialogDescription>
                    </DialogHeader>

                    {/* Blueprint Image */}
                    {blueprint?.imageUrl && (
                        <div className="relative w-full h-44 sm:h-52 md:h-60 overflow-hidden rounded-lg mb-4 border border-gray-200 bg-gray-50">
                            <div
                                aria-hidden="true"
                                className="absolute inset-0 bg-center bg-cover scale-110 blur-2xl opacity-70"
                                style={{ backgroundImage: `url(${blueprint.imageUrl})` }}
                            />
                            <img
                                src={blueprint.imageUrl}
                                alt={`${blueprint.name} blueprint`}
                                loading="lazy"
                                className="relative w-full h-full object-contain"
                            />
                            <button
                                type="button"
                                onClick={() => setLightboxOpen(true)}
                                aria-label={t("blueprints.viewLargerImage", "View larger image")}
                                className="absolute top-3 right-3 rounded-full bg-black/60 hover:bg-black/80 text-white p-1.5 transition-colors focus:outline-none focus:ring-2 focus:ring-white/50"
                            >
                                <ZoomIn className="h-4 w-4" />
                            </button>
                        </div>
                    )}

                    <div className="space-y-6">
                        {/* Blueprint Details */}
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 sm:p-6 border border-blue-200">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                                {/* Left Column - Basic Info */}
                                <div className="space-y-4">
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                            {t(
                                                "blueprints.blueprintDetails",
                                                "Blueprint Details"
                                            )}
                                        </h3>
                                        <div className="space-y-3">
                                            <div className="flex items-center gap-2">
                                                <Clock className="h-4 w-4 text-purple-500" />
                                                <span className="text-sm text-gray-600">
                                                    {t(
                                                        "blueprints.createdBy",
                                                        "Created by"
                                                    )}
                                                    :
                                                    <span className="font-medium text-purple-600 ml-1">
                                                        {blueprint?.creatorName}
                                                    </span>
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Users className="h-4 w-4 text-green-500" />
                                                <span className="text-sm text-gray-600">
                                                    {t(
                                                        "blueprints.downloads",
                                                        "Downloads"
                                                    )}
                                                    :
                                                    <span className="font-medium text-green-600 ml-1">
                                                        {Math.round(
                                                            ((blueprint?.downloadCount ||
                                                                0) /
                                                                1000) *
                                                                10
                                                        ) / 10}
                                                        K
                                                    </span>
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Star className="h-4 w-4 text-yellow-500" />
                                                <span className="text-sm text-gray-600">
                                                    {t(
                                                        "blueprints.visibility",
                                                        "Visibility"
                                                    )}
                                                    :
                                                    <Badge
                                                        variant="secondary"
                                                        className="ml-1"
                                                    >
                                                        {blueprint?.visibility?.toLowerCase()}
                                                    </Badge>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Right Column - Integration Types */}
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                        {t(
                                            "blueprints.requiredIntegrations",
                                            "Required Integrations"
                                        )}
                                    </h3>
                                    {blueprint?.integrationTypes &&
                                    blueprint.integrationTypes.length > 0 ? (
                                        <div className="flex flex-wrap gap-2">
                                            {blueprint.integrationTypes.map(
                                                (integrationType) => (
                                                    <Badge
                                                        key={integrationType}
                                                        variant="outline"
                                                        className="text-xs bg-white border-blue-300 text-blue-700"
                                                    >
                                                        {getIntegrationDisplayName(
                                                            integrationType
                                                        )}
                                                    </Badge>
                                                )
                                            )}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-gray-500">
                                            {t(
                                                "blueprints.noIntegrationsRequired",
                                                "This blueprint doesn't require any integrations."
                                            )}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Call to Action */}
                        <div className="text-center space-y-4">
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                <h4 className="text-lg font-semibold text-yellow-800 mb-2">
                                    {t(
                                        "blueprints.loginToCreate",
                                        "Login to Create Workflow"
                                    )}
                                </h4>
                                <p className="text-yellow-700 mb-4">
                                    {t(
                                        "blueprints.loginToCreateDescription",
                                        "You need to be logged in to create a workflow from this blueprint. Sign in or create an account to get started."
                                    )}
                                </p>
                                <Button
                                    onClick={handleLoginClick}
                                    size="lg"
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 sm:px-8 py-3 w-full sm:w-auto"
                                >
                                    <LogIn className="h-5 w-5 mr-2" />
                                    {t("common.login", "Login")}
                                </Button>
                            </div>
                        </div>

                        {/* Action buttons */}
                        <div className="flex justify-end pt-4">
                            <Button variant="outline" onClick={onClose}>
                                {t("common.close", "Close")}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            </>
        );
    }

    // Logged-in users modal
    return (
        <>
            <BlueprintImageLightbox
                imageUrl={blueprint?.imageUrl}
                alt={`${blueprint?.name || ""} blueprint`}
                isOpen={lightboxOpen}
                onClose={() => setLightboxOpen(false)}
            />
            {showIntegrationModal && selectedIntegrationType && (
                <NewIntegrationModal
                    onClose={() => {
                        setShowIntegrationModal(false);
                        setSelectedIntegrationType(null);
                    }}
                    onSuccess={() => {
                        setShowIntegrationModal(false);
                        setSelectedIntegrationType(null);
                        handleIntegrationCreated();
                    }}
                    INTEGRATION_TYPES={INTEGRATION_TYPES}
                    initialIntegrationType={selectedIntegrationType}
                    isEditing={false}
                    integrationToEdit={null}
                    setBrowserIntegration={() => undefined} // Not needed for this flow
                    isNameUniqueForType={() => true} // Allow any name for this flow
                />
            )}
            <Dialog open={isOpen} onOpenChange={onClose}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Workflow className="h-5 w-5" />
                            <span className="break-words">
                                {t(
                                    "blueprints.createWorkflowTitle",
                                    "Create Workflow"
                                )}
                            </span>
                        </DialogTitle>
                        <DialogDescription className="break-words">
                            {t("blueprints.createWorkflowDescription", {
                                name: blueprint?.name,
                            })}
                        </DialogDescription>
                    </DialogHeader>

                    {/* Blueprint Image */}
                    {blueprint?.imageUrl && (
                        <div className="relative w-full h-44 sm:h-52 md:h-60 overflow-hidden rounded-lg mb-4 border border-gray-200 bg-gray-50">
                            <div
                                aria-hidden="true"
                                className="absolute inset-0 bg-center bg-cover scale-110 blur-2xl opacity-70"
                                style={{ backgroundImage: `url(${blueprint.imageUrl})` }}
                            />
                            <img
                                src={blueprint.imageUrl}
                                alt={`${blueprint.name} blueprint`}
                                loading="lazy"
                                className="relative w-full h-full object-contain"
                            />
                            <button
                                type="button"
                                onClick={() => setLightboxOpen(true)}
                                aria-label={t("blueprints.viewLargerImage", "View larger image")}
                                className="absolute top-3 right-3 rounded-full bg-black/60 hover:bg-black/80 text-white p-1.5 transition-colors focus:outline-none focus:ring-2 focus:ring-white/50"
                            >
                                <ZoomIn className="h-4 w-4" />
                            </button>
                        </div>
                    )}

                    <div className="space-y-4">
                        {/* Checking integrations state */}
                        {checkingIntegrations && (
                            <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
                                <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                                <span className="text-blue-800">
                                    {t(
                                        "blueprints.checkingIntegrations",
                                        "Checking integration requirements..."
                                    )}
                                </span>
                            </div>
                        )}

                        {/* Integration status */}
                        {!checkingIntegrations && integrationStatus && (
                            <div className="space-y-3">
                                {integrationStatus.hasRequiredIntegrations ? (
                                    <Alert className="border-green-200 bg-green-50">
                                        <CheckCircle className="h-4 w-4 text-green-600" />
                                        <AlertTitle className="text-green-800">
                                            {t(
                                                "blueprints.integrationsReady",
                                                "All integrations ready"
                                            )}
                                        </AlertTitle>
                                        <AlertDescription className="text-green-700">
                                            {blueprint?.integrationTypes
                                                ?.length > 0
                                                ? t(
                                                      "blueprints.requiredIntegrationsFound",
                                                      {
                                                          count: integrationStatus.totalRequired,
                                                      }
                                                  )
                                                : t(
                                                      "blueprints.noIntegrationsRequired",
                                                      "This blueprint doesn't require any integrations."
                                                  )}
                                        </AlertDescription>
                                    </Alert>
                                ) : (
                                    <Alert className="border-orange-200 bg-orange-50">
                                        <AlertCircle className="h-4 w-4 text-orange-600" />
                                        <AlertTitle className="text-orange-800">
                                            {t(
                                                "blueprints.missingIntegrations",
                                                "Missing integrations"
                                            )}
                                        </AlertTitle>
                                        <AlertDescription className="text-orange-700">
                                            {t(
                                                "blueprints.missingIntegrationsDescription",
                                                {
                                                    count: integrationStatus.totalRequired,
                                                    countWord: integrationStatus.totalRequired === 1 ? t("blueprints.integrationSingular") : t("blueprints.integrationPlural"),
                                                    missing: integrationStatus.totalMissing,
                                                    missingVerb: integrationStatus.totalMissing === 1 ? t("blueprints.missingVerbSingular") : t("blueprints.missingVerbPlural"),
                                                }
                                            )}
                                        </AlertDescription>
                                    </Alert>
                                )}

                                {/* Show required integrations */}
                                {blueprint?.integrationTypes?.length > 0 && (
                                    <div>
                                        <h4 className="text-sm font-medium mb-2">
                                            {t(
                                                "blueprints.requiredIntegrations",
                                                "Required integrations:"
                                            )}
                                        </h4>
                                        <div className="flex flex-wrap gap-2">
                                            {blueprint.integrationTypes.map(
                                                (integrationType) => {
                                                    const isAvailable =
                                                        integrationStatus.availableIntegrations.some(
                                                            (integration) =>
                                                                integration.type ===
                                                                integrationType
                                                        );
                                                    return (
                                                        <Badge
                                                            key={
                                                                integrationType
                                                            }
                                                            variant={
                                                                isAvailable
                                                                    ? "default"
                                                                    : "destructive"
                                                            }
                                                            className="text-xs"
                                                        >
                                                            {isAvailable && (
                                                                <CheckCircle className="h-3 w-3 mr-1" />
                                                            )}
                                                            {!isAvailable && (
                                                                <X className="h-3 w-3 mr-1" />
                                                            )}
                                                            {getIntegrationDisplayName(
                                                                integrationType
                                                            )}
                                                        </Badge>
                                                    );
                                                }
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Badge management for support/super-admin */}
                        <BlueprintBadgeToggle
                            blueprint={blueprint}
                            onBadgeUpdated={(badges) => onBadgeUpdated?.(blueprint.id, badges)}
                        />

                        {/* Action buttons */}
                        <div className="flex flex-col sm:flex-row gap-2 pt-4">
                            <Button
                                variant="outline"
                                onClick={onClose}
                                className="flex-1 order-2 sm:order-1"
                            >
                                {t("common.cancel", "Cancel")}
                            </Button>

                            {!checkingIntegrations && integrationStatus && (
                                <>
                                    {integrationStatus.hasRequiredIntegrations ? (
                                        <Button
                                            onClick={handleCreateWorkflow}
                                            disabled={creatingWorkflow}
                                            className="flex-1 order-1 sm:order-2"
                                        >
                                            {creatingWorkflow && (
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            )}
                                            {t(
                                                "blueprints.createWorkflow",
                                                "Create Workflow"
                                            )}
                                        </Button>
                                    ) : (
                                        <Button
                                            onClick={handleSetupNextIntegration}
                                            className="flex-1 order-1 sm:order-2"
                                        >
                                            <Plus className="h-4 w-4 mr-2" />
                                            {t("blueprints.setupIntegrations", {
                                                count: integrationStatus.totalMissing,
                                                plural:
                                                    integrationStatus.totalMissing !==
                                                    1
                                                        ? "s"
                                                        : "",
                                            })}
                                        </Button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Integration setup modal */}
        </>
    );
}
